/* eslint-disable no-undef */
/* eslint-disable implicit-arrow-linebreak */
import { isFunction } from 'lodash';
import * as Sentry from '@sentry/nextjs';

import { getUserExperimentId } from 'helpers/experiments';

// Helpers for mParticle (collects customer data for analysis/segmentation)
// https://docs.mparticle.com/

export const mParticleEventTypes = {
  Navigation: 1,
  Location: 2,
  Search: 3,
  Transaction: 4,
  UserContent: 5,
  UserPreference: 6,
  Social: 7,
  Other: 8,
};

export const mParticleCustomEvents = {
  assessFeatureFlag: 'assess_feature_flag',
  attemptAuthentication: 'attempt_authentication',
  completeRatingsSurvey: 'complete_ratings_survey',
  navigateCarousel: 'navigate_carousel',
  performLocationSearch: 'perform_location_search',
  restaurantSurveyModalClose: 'restaurant_survey_modal_close',
  seletApplyFilter: 'select_apply_filter',
  selectCarouselViewAll: 'select_carousel_view_all',
  selectCustomerSupport: 'select_customer_support',
  selectDownloadApp: 'select_download_app',
  selectForgotPassword: 'select_forgot_password',
  selectFulfillmentToggle: 'select_fulfillment_toggle',
  selectLoginEmail: 'select_login_email',
  selectRestaurant: 'select_restaurant',
  selectRestaurantRating: 'select_restaurant_rating',
  selectRestaurantReorderChoice: 'select_restaurant_reorder_choice',
  selectSignup: 'select_signup',
  selectSignupEmail: 'select_signup_email',
  selectSocial: 'select_social',
  selectX: 'select_x',
  viewErrorPage: 'view_error_page',
  viewRestaurantResults: 'view_restaurant_results',
  viewRatingsSurvey: 'view_ratings_survey',
  viewWebHomepage: 'view_web_homepage',
};

export function getFullAttributes(attributes) {
  const userExperimentId = getUserExperimentId();
  // Add Hermosa Marketplace Id & Experiment Key
  const fullAttributes = {
    ...attributes,
    experiment_key: userExperimentId,
    hermosa_marketplace_id: '2',
    user_is_authenticated: attributes.user_is_authenticated || false,
  };
  return fullAttributes;
}

// eslint-disable-next-line arrow-body-style
export const mParticlePageView = (pageName, attributes) => {
  const fullAttributes = getFullAttributes(attributes);
  return window.mParticle?.logPageView(pageName, fullAttributes);
};

export const logMparticleEvent = ({ eventName, eventType, attributes }) => {
  const fullAttributes = getFullAttributes(attributes || {});
  mParticle?.logEvent(eventName, eventType, fullAttributes);
};

export const loginMparticleUser = (user, cb) => {
  // mParticle expects an identityRequest object on login/logout
  // https://docs.mparticle.com/developers/sdk/javascript/getting-started/
  const currentUser = mParticle.Identity.getCurrentUser();
  if (!currentUser || !currentUser.isLoggedIn()) {
    // We can't find a current user - create a new identity and pass to login API
    const userIdentities = {
      email: user.email,
      customerid: user.id,
    };

    const identityRequest = {
      userIdentities,
    };

    mParticle.Identity.login(identityRequest, (result) => {
      if (result.httpCode === 200) {
        if (isFunction(cb)) {
          cb(null, 'success');
        }
        if (user.phone) {
          currentUser.setUserAttribute('$Mobile', user.phone.number);
        }
        currentUser.setUserAttribute('user_is_optout', user.is_optout);
      } else {
        Sentry.captureException(`mParticle login fail: ${result.body}`);
        if (isFunction(cb)) {
          cb(result.body, 'failure');
        }
      }
    });
  } else if (isFunction(cb)) {
    cb(null, 'success');
  }
};

export const logoutMparticleUser = () => {
  // mParticle requires passing an empty user identity object to clear session
  const identityRequest = {
    userIdentities: {},
  };
  mParticle.Identity.logout(identityRequest);
};

export function handleAppLinkClick({ user, source }) {
  logMparticleEvent({
    eventName: mParticleCustomEvents.selectDownloadApp,
    eventType: mParticleEventTypes.Other,
    attributes: {
      source,
      user_is_authenticated: !!user,
      mobile_app_type: 'marketplace_app',
    },
  });
  window.mParticle.upload();
}

export function logSelectRestaurantEvent({ cardSource, index, restaurant, user }) {
  const cardLocation = index === null ? 0 : index + 1;
  const hasCoverPhoto = !!restaurant.media?.cover_image_url;

  logMparticleEvent({
    eventName: mParticleCustomEvents.selectRestaurant,
    eventType: mParticleEventTypes.Other,
    attributes: {
      card_source: cardSource,
      card_location: cardLocation,
      restaurant_location_id: restaurant.id,
      has_cover_photo: hasCoverPhoto,
      has_auto_promo: !!restaurant.has_automatic_discount,
      user_is_authenticated: !!user,
    },
  });
  window.mParticle.upload();
}

export function selectSocialEvent(method) {
  logMparticleEvent({
    eventName: mParticleCustomEvents.selectSocial,
    eventType: mParticleEventTypes.Other,
    attributes: {
      authentication_method: method,
    },
  });
}
